import * as React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle } from "lucide-react";
import { authService } from "@/services/authService";
import type { User } from "@/services/authService";

interface PhoneStepProps {
  userData: User | null;
  onSuccess: () => void;
  onError: (msg: string) => void;
}

export function PhoneStep({ userData, onSuccess, onError }: PhoneStepProps) {
  const [noHp, setNoHp] = React.useState(userData?.no_hp ?? "");
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    const trimmed = noHp.trim();
    if (!trimmed) {
      setError("Nomor HP tidak boleh kosong");
      return;
    }

    const phoneRegex = /^(\+62|08)\d{7,13}$/;
    if (!phoneRegex.test(trimmed)) {
      setError("Format nomor HP tidak valid (contoh: 08123456789 atau +6281234567890)");
      return;
    }

    setIsLoading(true);
    try {
      await authService.updateProfile({ no_hp: trimmed });
      onSuccess();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Gagal menyimpan nomor HP";
      setError(msg);
      onError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-start gap-2 rounded-lg border border-blue-300 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20">
        <span className="mt-0.5 text-blue-600 dark:text-blue-400">ℹ</span>
        <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
          Masukkan nomor HP aktif Anda untuk keperluan komunikasi.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="no_hp">Nomor HP</Label>
        <Input
          id="no_hp"
          type="tel"
          inputMode="tel"
          value={noHp}
          onChange={(e) => setNoHp(e.target.value)}
          placeholder="Contoh: 08123456789"
          disabled={isLoading}
        />
        <p className="text-xs text-muted-foreground">
          Format: 08xxxxxxxxxx atau +62xxxxxxxxxx
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-md">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex gap-2">
        <Button type="submit" className="flex-1" disabled={isLoading || !noHp.trim()}>
          {isLoading ? "Menyimpan..." : "Lanjutkan"}
        </Button>
      </div>
    </form>
  );
}
